import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import useBenguide from 'hooks/benguide';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import useUPClient from 'modules/clients/UPClient/useUPClient';
import PlanCardEditor from 'modules/benefitsWellness/components/PlanCardEditor';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  fetchVolBenefitsPlanList,
  getRichMediaContent,
  reloadVoluntaryBenefitsSection,
  updateRichMediaContent,
} from 'modules/benefitsWellness/slices/voluntaryBenefitsSlice';
import ContentPreviewer from 'components/ContentPreviewer/ContentPreviewer';
import {
  OPEN_VOLUNTARY_BENEFITS_EDIT_MODAL,
  VOLUNTARY_BENEFITS_CHANNEL,
} from 'modules/clients/UPClient/UPClientConst';
import iconEdit from 'images/icon-edit.svg';
import VoluntaryBenefitsPlan from 'model/VoluntaryBenefitsPlan';
import TooltipPopover from 'components/TooltipPopover/TooltipPopover';
import Spinner from 'components/Spinner/Spinner';
import SavePlanDescriptionModal from 'components/SavePlanDescriptionModal/SavePlanDescriptionModal';
import AddYourText from 'modules/benefitsWellness/components/AddYourText/AddYourText';
import { SectionName } from 'modules/home/constants';
import { ReactComponent as LeftArrowIcon } from 'images/icons/Icon-left-arrow.svg';
import { ReactComponent as RightArrowIcon } from 'images/icons/Icon-right-arrow.svg';
import { benefitCategory } from 'modules/auth/constants/commonConstants';
import PlanCardContainer from 'modules/benefitsWellness/components/PlanCardContainer/PlanCardContainer';
import BenefitCarrier from 'model/BenefitCarrier';
import ViewResourcesModal from 'components/ViewResourcesModal/ViewResourcesModal';
import { VoluntaryPlanTag } from 'modules/benefitsWellness/components/VoluntaryTag/voluntaryPlanTag';
import { PLAN_RESOURCES } from 'constants/languageConstants';
import { getText } from 'utils/stringUtil';
import styles from './voluntaryBenefitsCard.module.less';

type VoluntaryBenefitsCardProps = {
  plan: VoluntaryBenefitsPlan;
  moveBottom: () => void;
  moveTop: () => void;
  hideTop: boolean;
  hideBottom: boolean;
  renderReorder: boolean;
  dataIndex?: number;
};

const VoluntaryBenefitsCard: FC<VoluntaryBenefitsCardProps> = ({
  plan,
  moveBottom,
  moveTop,
  hideTop,
  hideBottom,
  renderReorder,
  dataIndex,
}: VoluntaryBenefitsCardProps) => {
  const upClient = useUPClient();
  const benguide = useBenguide();
  const dispatch = useAppDispatch();
  const { isTablet } = useAppSelector((state) => state.layout);
  const { richMedia } = useAppSelector((state) => state.voluntaryBenefits);
  const defaultRichMedia = useAppSelector(
    (state) => state.voluntaryBenefits.defaultRichMedia.data
  );
  const language = useAppSelector((state) => state.benguide.data?.language);
  const { masterId, isEditMode } = benguide;
  const defaultContent = '';
  const { id, benefitKind, benefitCarrier = {} as BenefitCarrier } = plan;
  const { benefitDescription = '' } = benefitCarrier;
  const richMediaForPlan = get(richMedia, id);
  const { content, inProgress: isRichMediaLoading } = richMediaForPlan || {};

  const isContentReceived = content !== undefined;

  const [isVisible, setIsVisible] = useState(false);
  const [
    isSavePlanDescriptionModalVisible,
    setIsSavePlanDescriptionModalVisible,
  ] = useState<boolean>(false);
  const [editorContent, setEditorContent] = useState<string>(defaultContent);
  const planCardEditorRef = useRef<any>();
  const loadContent = useCallback(() => {
    dispatch(getRichMediaContent(masterId, id, benefitKind));
  }, [dispatch, masterId, id, benefitKind]);

  const showAddContentBtn = useMemo(() => {
    return isEmpty(content) && isEditMode;
  }, [content, isEditMode]);

  const saveContent = (content: string) => {
    dispatch(
      updateRichMediaContent(masterId, id, content, benefitKind, () => {
        dispatch(
          reloadVoluntaryBenefitsSection(
            benguide.masterId,
            benguide.latestRevision
          )
        );
        dispatch(
          fetchVolBenefitsPlanList(
            benguide.employerId,
            benguide.planYearId,
            benguide.benefitClasses
          )
        );
        setIsSavePlanDescriptionModalVisible(false);
      })
    );
  };

  useEffect(() => {
    loadContent();
  }, [loadContent, plan?.description]);

  useEffect(() => {
    setEditorContent(content || defaultContent);
  }, [content]);

  const getDefaultRichMediaForSection = () => {
    let sectionName = SectionName.ACCIDENT;
    if (benefitKind === benefitCategory.CRITICAL_ILLNESS.value) {
      sectionName = SectionName.CRITICAL_ILLNESS;
    } else if (benefitKind === benefitCategory.HOSPITAL.value) {
      sectionName = SectionName.HOSPITAL;
    } else if (benefitKind === benefitCategory.CUSTOM_VOLUNTARY_BENEFIT.value) {
      sectionName = SectionName.CUSTOM_VOLUNTARY_BENEFIT;
    }
    if (sectionName && defaultRichMedia[sectionName]) {
      return defaultRichMedia[sectionName];
    }
    return '';
  };

  const handleResetDefault = () => {
    const defaultContent =
      benefitDescription || getDefaultRichMediaForSection();
    setEditorContent(defaultContent);
    planCardEditorRef.current.changeContent(defaultContent);
  };

  const setCurrentConetnt = () => {
    setEditorContent(content);
  };

  const handleDescriptionEditSubmit = (content: string) => {
    const isDescriptionEdited = plan && plan.description !== content;
    // We dont saveContent() if description is not edited as it is useless API call
    if (isDescriptionEdited) {
      setIsSavePlanDescriptionModalVisible(true);
    }
  };

  const renderHoverMoveOverlay = () => (
    <div className={styles.moveOverlay}>
      {/* Top Section */}
      {!hideTop ? (
        <div className={styles.top} onClick={moveTop}>
          <LeftArrowIcon className={styles.topIcon} />
          <label>Move</label>
        </div>
      ) : (
        <div className={styles.emptyDiv}></div>
      )}

      {/* Bottom Section */}
      {!hideBottom ? (
        <div className={styles.bottom} onClick={moveBottom}>
          <label>Move</label>
          <RightArrowIcon className={styles.bottomIcon} />
        </div>
      ) : (
        <div className={styles.emptyDiv}></div>
      )}
    </div>
  );

  return (
    <div className={styles.volBenefitsCardWrapper}>
      {renderReorder && (
        <div className={styles.moveWrapper}>
          <EditableHotspot
            alwaysVisible={false}
            className={styles.hotSpotHoverContainer}
            customModal={() => <></>}
            overlayComponent={renderHoverMoveOverlay()}
          >
            <div className={styles.moveArea}></div>
          </EditableHotspot>
        </div>
      )}
      <PlanCardContainer>
        <>
          {isTablet && <div className={styles.topShadow}></div>}
          <div className={styles.summaryWrapper}>
            <EditableHotspot
              alwaysVisible={false}
              editText="Edit"
              editIcon={<img src={iconEdit} alt="edit" />}
              useChannel={{
                client: upClient,
                channel: VOLUNTARY_BENEFITS_CHANNEL,
                event: OPEN_VOLUNTARY_BENEFITS_EDIT_MODAL,
                data: plan,
              }}
              dataTestId={`voluntaryBenefits-plan${dataIndex}-name`}
            >
              <div className={styles.summary}>
                <div className={styles.planHeaderWrapper}>
                  <div className={styles.logoWrapper}>
                    {plan.benefitCarrier && (
                      <img
                        src={plan.benefitCarrier.logoUrl}
                        alt={`${plan.benefitCarrier.name} logo`}
                        className={styles.logo}
                      />
                    )}
                  </div>
                  <TooltipPopover content={plan.name} placement="right">
                    <div className={styles.planName}>{plan.name}</div>
                  </TooltipPopover>
                  <div className={styles.volBenefitWrapper}>
                    <VoluntaryPlanTag />
                  </div>
                </div>
                {(!isEmpty(plan.documents) ||
                  !isEmpty(plan.additionalDocuments) ||
                  !isEmpty(plan.additionalWeblinks)) && (
                  <div
                    className={styles.viewDetailsLink}
                    onClick={() => setIsVisible(true)}
                  >
                    {getText(PLAN_RESOURCES, language)}
                  </div>
                )}
              </div>
            </EditableHotspot>
          </div>
        </>
      </PlanCardContainer>
      <div
        className={
          showAddContentBtn ? styles.addTextWrapper : styles.description
        }
      >
        {!isContentReceived ? (
          <Spinner />
        ) : (
          <EditableHotspot
            className={showAddContentBtn ? styles.addTextHotspot : ''}
            alwaysVisible={showAddContentBtn}
            editText={showAddContentBtn ? '' : 'Edit'}
            editIcon={
              showAddContentBtn ? <></> : <img src={iconEdit} alt="edit" />
            }
            customModal={(
              visible: boolean,
              onClose: () => {},
              editorKey: string
            ) => (
              <PlanCardEditor
                isOpenModal={visible}
                closeModal={onClose}
                editorContent={editorContent}
                isResetEnabled={true}
                resetToCurrentValue={() => {}}
                resetToDefault={handleResetDefault}
                defaultContent={getDefaultRichMediaForSection()}
                updateEditorContent={setEditorContent}
                benguideId={masterId}
                onSubmit={handleDescriptionEditSubmit}
                ref={planCardEditorRef}
                setCurrentConetnt={setCurrentConetnt}
                editorKey={editorKey}
              />
            )}
            dataTestId={`voluntaryBenefits-plan${dataIndex}-description`}
          >
            {showAddContentBtn ? (
              <AddYourText />
            ) : (
              <div className={styles.content}>
                <ContentPreviewer
                  htmlContent={
                    isEmpty(editorContent) ? defaultContent : editorContent
                  }
                />
              </div>
            )}
          </EditableHotspot>
        )}
      </div>
      <ViewResourcesModal
        plan={plan}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        benefitKind={benefitKind}
      />
      <SavePlanDescriptionModal
        confirmLoading={isRichMediaLoading}
        isVisible={isSavePlanDescriptionModalVisible}
        onClose={() => {
          setIsSavePlanDescriptionModalVisible(false);
          setEditorContent(plan?.description || '');
        }}
        onConfirm={() => saveContent(editorContent)}
      />
    </div>
  );
};
export default VoluntaryBenefitsCard;
